import React from "react";
import { styled, useTheme } from "@mui/material/styles";
import {
  Avatar,
  Box,
  Card,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Typography,
  Stack,
} from "@mui/material";

import MainCard from "../../components/MainCard";
import CancelIcon from "@mui/icons-material/Cancel";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CategoryIcon from "@mui/icons-material/Category";
import DoNotDisturbOnIcon from "@mui/icons-material/DoNotDisturbOn";

const CardType = {
  total: {
    label: "Total Execution",
    icon: CategoryIcon,
    color: ["primary", "dark"],
    bgColor: "linear-gradient(135deg, #E3F2FD 0%, #BBDEFB 100%)",
    accent: "#1976D2"
  },
  success: {
    label: "Passed",
    icon: CheckCircleIcon,
    color: ["success", "dark"],
    bgColor: "linear-gradient(135deg, #E8F5E9 0%, #C8E6C9 100%)",
    accent: "#2E7D32"
  },
  fail: {
    label: "Failed",
    icon: CancelIcon,
    color: ["error", "dark"],
    bgColor: "linear-gradient(135deg, #FFEBEE 0%, #FFCDD2 100%)",
    accent: "#D32F2F"
  },
  skip: {
    label: "Skipped",
    icon: DoNotDisturbOnIcon,
    color: ["warning", "dark"],
    bgColor: "linear-gradient(135deg, #FFF3E0 0%, #FFE0B2 100%)",
    accent: "#EF6C00"
  },
  flaky: {
    label: "Flaky",
    icon: CheckCircleIcon,
    color: ["info", "dark"],
    bgColor: "linear-gradient(135deg, #E1F5FE 0%, #B3E5FC 100%)",
    accent: "#0288D1"
  },
  rate: {
    label: "Success Rate",
    icon: CheckCircleIcon,
    color: ["secondary", "dark"],
    bgColor: "linear-gradient(135deg, #F3E5F5 0%, #E1BEEF 100%)",
    accent: "#7B1FA2"
  },
};

const ResultCard = ({ isAPIReport, isLoading, cardType, count, onClick }) => {
  const theme = useTheme();
  const config = CardType[cardType];

  const CardWrapper = styled(MainCard)(({ theme }) => ({
    overflow: "hidden",
    position: "relative",
    width: "100%",
    background: config.bgColor,
    border: 'none',
    boxShadow: '0 4px 12px rgba(0,0,0,0.05)',
    transition: 'all 0.3s cubic-bezier(0.25, 0.8, 0.25, 1)',
    '&:hover': {
      boxShadow: onClick ? '0 12px 28px rgba(0,0,0,0.12)' : '0 4px 12px rgba(0,0,0,0.05)',
      transform: onClick ? 'translateY(-4px)' : 'none'
    },
    "&:after": {
      content: '""',
      position: "absolute",
      width: "80px",
      height: "80px",
      background: 'rgba(255, 255, 255, 0.3)',
      borderRadius: "50%",
      top: "-25px",
      right: "-25px",
    },
    "&:before": {
      content: '""',
      position: "absolute",
      width: "50px",
      height: "50px",
      background: 'rgba(255, 255, 255, 0.2)',
      borderRadius: "50%",
      bottom: "-15px",
      left: "10%",
      zindex: 0
    }
  }));

  return (
    <>
      {isLoading ? (
        <CardWrapper>
          <Box sx={{ p: 1.5 }}>
            <Typography variant="h3">Loading...</Typography>
          </Box>
        </CardWrapper>
      ) : (
        <CardWrapper border={false} content={false} onClick={onClick} sx={{ cursor: onClick ? "pointer" : "default" }}>
          <Box sx={{ p: 1.5, height: '100%', position: 'relative', zIndex: 1 }}>
            <Stack spacing={0.25}>
              <Typography
                sx={{
                  color: config.accent,
                  fontWeight: 700,
                  fontSize: '0.8rem',
                  textTransform: 'uppercase',
                  letterSpacing: '0.05em',
                  opacity: 0.8
                }}>
                {config.label}
              </Typography>
              <Typography
                variant="h1"
                sx={{
                  color: config.accent,
                  fontWeight: 900,
                  fontSize: '1.6rem',
                  lineHeight: 1,
                  textShadow: '0 2px 4px rgba(0,0,0,0.05)'
                }}>
                {count}{cardType === 'rate' ? '%' : ''}
              </Typography>
            </Stack>
          </Box>
        </CardWrapper>
      )}
    </>
  );
};

export { ResultCard, CardType };
